import { useEffect, useId, useRef, useState } from "react"
import type { DocView } from "../docs.store"
import "./attachTray.css"

export interface TrayItem { id: string; doc: DocView }

/** Each state stays on screen at least this long, so fast documents still show their count moving. */
export const HOLD_MS = 400

export type TrayPhase = "prepare" | "reading" | "summarise" | "ready" | "failed"

/** What one row shows. `word` is the state name announced to screen readers; `label` is the visible line. */
export interface TrayView {
  phase: TrayPhase
  word: string
  label: string
  count: string
  fraction: number
  done: number
  total: number
  warn: string
}

function failureWord(doc: DocView): string {
  const named = /page\s+(\d+)/i.exec(doc.error ?? "")
  if (named) return `Couldn't read page ${named[1]}`
  if (doc.pagesFailed > 1) return `Couldn't read ${doc.pagesFailed} pages`
  return "Couldn't read this page"
}

/** Status words and numbers for one document. Counts come only from pages_total / pages_done set by the server. */
export function describeDoc(doc: DocView): TrayView {
  const total = doc.pagesTotal
  const done = doc.pagesDone
  const fraction = total > 0 ? Math.min(1, done / total) : 0
  const warn = doc.pagesFailed > 0 ? `${doc.pagesFailed} page(s) unreadable` : ""
  const count = total > 0 ? `${done}/${total}` : ""
  const base = { done, total, warn }

  if (doc.status === "failed") {
    const word = failureWord(doc)
    return { ...base, phase: "failed", word, label: word, count, fraction }
  }
  if (doc.status === "ready") {
    const label = doc.pagesFailed > 0 ? `${done} of ${total} pages read` : `${total} pages read`
    return { ...base, phase: "ready", word: "Ready", label, count: "", fraction: 1 }
  }
  if (doc.status === "summarising") {
    return { ...base, phase: "summarise", word: "Summarising", label: `Summarising ${total} pages`, count, fraction: 1 }
  }
  if (doc.status === "indexing" && total > 0) {
    const page = Math.min(done + 1, total)
    return { ...base, phase: "reading", word: "Reading", label: `Reading page ${page} of ${total}`, count, fraction }
  }
  return { ...base, phase: "prepare", word: "Preparing pages", label: "Preparing pages", count: "", fraction: 0 }
}

/** Shows `target`, but keeps each phase on screen for HOLD_MS before moving to the next one. Count-only changes apply at once. */
export function useHeldView(target: TrayView): TrayView {
  const [shown, setShown] = useState(target)
  const shownRef = useRef(target)
  const shownAt = useRef(Date.now())
  const latest = useRef(target)
  latest.current = target
  const { phase, done, total, word, label, count, fraction, warn } = target

  useEffect(() => {
    const next = latest.current
    if (next.phase === shownRef.current.phase) {
      shownRef.current = next
      setShown(next)
      return
    }
    const wait = Math.max(0, HOLD_MS - (Date.now() - shownAt.current))
    const timer = setTimeout(() => {
      shownAt.current = Date.now()
      shownRef.current = latest.current
      setShown(latest.current)
    }, wait)
    return () => clearTimeout(timer)
  }, [phase, done, total, word, label, count, fraction, warn])

  return shown
}

function TrayRow({ id, doc, nameId, onRemove }: { id: string; doc: DocView; nameId: string; onRemove?: (id: string) => void }) {
  const view = useHeldView(describeDoc(doc))
  const determinate = view.phase !== "prepare"
  const pct = Math.round(view.fraction * 100)
  const failed = view.phase === "failed"
  return (
    <li className={`nl-tray-row${failed ? " failed" : ""}`} data-phase={view.phase} data-testid="tray-row">
      <div className="nl-tray-main">
        <span className="nl-tray-name" id={nameId}>{doc.filename}</span>
        <span className="nl-tray-meta">
          <span className="nl-tray-word">
            {view.phase === "ready" ? (
              <svg className="nl-tray-check" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
                <path d="M2 6.5 4.5 9 10 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
            {view.label}
          </span>
          {view.count ? <span className="nl-tray-count">{view.count}</span> : null}
        </span>
        {view.warn ? <span className="nl-tray-warn">{view.warn}</span> : null}
        <div
          className={`nl-prog${determinate ? "" : " is-indeterminate"}`}
          role="progressbar"
          aria-labelledby={nameId}
          aria-valuemin={0}
          aria-valuemax={100}
          {...(determinate ? { "aria-valuenow": pct, "aria-valuetext": `${view.done} of ${view.total} pages` } : {})}
        >
          <i className="nl-tray-bar" style={determinate ? { width: `${pct}%` } : undefined} />
        </div>
      </div>
      {onRemove ? (
        <button type="button" className="nl-tray-x" aria-label={`Remove ${doc.filename}`} onClick={() => onRemove(id)}>
          ×
        </button>
      ) : null}
    </li>
  )
}

/** Per-file rows with page progress. A visually hidden live region announces only state changes, not each page. */
export function AttachTray({ items, onRemove, notice }: { items: TrayItem[]; onRemove?: (id: string) => void; notice?: string }) {
  const uid = useId()
  const prevWords = useRef<Record<string, string>>({})
  const [announce, setAnnounce] = useState("")

  useEffect(() => {
    const next: Record<string, string> = {}
    const changed: string[] = []
    for (const { id, doc } of items) {
      const word = describeDoc(doc).word
      next[id] = word
      if (prevWords.current[id] !== undefined && prevWords.current[id] !== word) changed.push(`${doc.filename}: ${word}`)
    }
    prevWords.current = next
    if (changed.length) setAnnounce(changed.join(". "))
  }, [items])

  if (items.length === 0) return null
  return (
    <div className="nl-tray-wrap" data-testid="attach-tray">
      <ul className="nl-tray" aria-label="Attached documents">
        {items.map(({ id, doc }) => (
          <TrayRow key={id} id={id} doc={doc} nameId={`${uid}-${id}`} onRemove={onRemove} />
        ))}
      </ul>
      {notice ? <p className="nl-tray-notice">{notice}</p> : null}
      <div className="nl-vh" role="status" aria-live="polite" data-testid="tray-announce">{announce}</div>
    </div>
  )
}
